import { useCallback, useState, type FocusEvent, type ReactNode } from 'react';
import { RichEditor, type RichEditorImageUploadHandler, type TiptapDoc } from '@odo/shared-ui';
import type {
    PlannableTriggerKind,
    VirtualAppointmentTeam
} from '../../services/virtualAppointments';
import { FieldLabel } from './VirtualAppointmentFieldParts';
import { VirtualAppointmentTeamSelector } from './VirtualAppointmentTeamSelector';
import {
    VirtualAppointmentTypeField,
    type VirtualAppointmentTypeOptions
} from './VirtualAppointmentTypeField';
import {
    VirtualAppointmentTriggerField,
    type WeeksFromChoice
} from './VirtualAppointmentTriggerField';
import { VirtualAppointmentDescriptionBox } from './VirtualAppointmentDetailPanels';
import { VirtualAppointmentAssigneeField } from './VirtualAppointmentAssignee';
import { parseWeeksInput } from './virtualAppointmentTriggerInput';
import {
    parsedTrigger,
    type VirtualAppointmentDraft,
    type VirtualAppointmentDraftErrors,
    type VirtualAppointmentDraftPatch
} from './virtualAppointmentDraft';
import { todayUtcIso } from './virtualAppointmentWeeks';
import { nogosUnavailableHint } from './virtualAppointmentNogos';
import {
    plannedTriggerBaseline,
    UNKNOWN_ALIGNER_PROGRESS,
    type AlignerProgress
} from './virtualAppointmentTriggerBaseline';
import type { SavedNogoBaseline } from './virtualAppointmentNogoBaseline';
import {
    keptWeeksAnchor,
    NO_SAVED_TRIGGERS,
    type SavedTriggers
} from './virtualAppointmentTriggerAhead';

interface VirtualAppointmentEditorProps {
    draft: VirtualAppointmentDraft;
    errors: VirtualAppointmentDraftErrors;
    typeOptions: VirtualAppointmentTypeOptions;
    /** Show errors before the user leaves the panel, e.g. for rows that were already there. */
    revealErrors: boolean;
    onChange: (key: string, patch: VirtualAppointmentDraftPatch) => void;
    onImageUpload: RichEditorImageUploadHandler;
    /** For a VA opened straight away: no trigger to pick, the type takes the whole row. */
    hideTrigger?: boolean;
    /** Type and trigger each take a whole row, so the trigger's kind and value sit side by side. */
    stackTypeAndTrigger?: boolean;
    /** UTC day the Treatment phase began, which week triggers count from; null when unknown. */
    treatmentStartDate?: string | null;
    /** Whether the patient is linked to Dental Monitoring; without it No-gos is disabled. */
    dmLinked?: boolean;
    /** Set with no treatment start on record: a week can only count from now, and this says why. */
    treatmentStartUnavailableHint?: string;
    /** False where week N must count from the treatment start, as on a refinement's next series. */
    offerWeeksFromNow?: boolean;
    /** Saved triggers by VA id, so an unchanged "from now" week shows the anchor it keeps. */
    savedTriggers?: SavedTriggers;
    /** Shown under an aligner trigger, when known. */
    aligners?: AlignerProgress;
    /** The saved VA's running no-go count and window, when it already counts no-gos. */
    savedNogo?: SavedNogoBaseline;
    /** Rendered above the fields, inside the scrolling panel. */
    leading?: ReactNode;
}

interface TeamFieldProps {
    team: VirtualAppointmentTeam;
    onChange: (team: VirtualAppointmentTeam) => void;
}

function TeamField({ team, onChange }: TeamFieldProps) {
    return (
        <div className="flex flex-col">
            <FieldLabel required>Team</FieldLabel>
            <VirtualAppointmentTeamSelector value={team} onChange={onChange} />
        </div>
    );
}

interface CompactFieldsProps {
    draft: VirtualAppointmentDraft;
    errors: VirtualAppointmentDraftErrors;
    typeOptions: VirtualAppointmentTypeOptions;
    change: (patch: VirtualAppointmentDraftPatch) => void;
    hideTrigger: boolean;
    stackTypeAndTrigger: boolean;
    triggerBaseline: string | null;
    dmLinked: boolean;
    weeksFrom?: WeeksFromChoice;
}

function CompactFields({
    draft,
    errors,
    typeOptions,
    change,
    hideTrigger,
    stackTypeAndTrigger,
    triggerBaseline,
    dmLinked,
    weeksFrom
}: CompactFieldsProps) {
    const { key } = draft;
    const typeTriggerLayout =
        hideTrigger || stackTypeAndTrigger ? 'flex flex-col gap-4' : 'grid grid-cols-2 gap-3';
    return (
        <div className="flex-shrink-0 flex flex-col gap-4">
            <div className={typeTriggerLayout}>
                <VirtualAppointmentTypeField
                    inputId={`va-type-${key}`}
                    typeMode={draft.typeMode}
                    typeCode={draft.typeCode}
                    typeCustom={draft.typeCustom}
                    options={typeOptions}
                    error={errors.type}
                    onSelectCode={(code) => change({ typeMode: 'code', typeCode: code })}
                    onSelectCustom={() => change({ typeMode: 'custom' })}
                    onCustomChange={(value) => change({ typeCustom: value })}
                />
                {!hideTrigger && (
                    <VirtualAppointmentTriggerField
                        inputId={`va-trigger-${key}`}
                        triggerKind={draft.triggerKind}
                        triggerAligner={draft.triggerAligner}
                        triggerWeeks={draft.triggerWeeks}
                        triggerNogos={draft.triggerNogos}
                        error={errors.trigger}
                        weeksFrom={weeksFrom}
                        nogosUnavailableHint={nogosUnavailableHint(dmLinked)}
                        baseline={triggerBaseline}
                        onKindChange={(kind: PlannableTriggerKind) => change({ triggerKind: kind })}
                        onAlignerChange={(value) => change({ triggerAligner: value })}
                        onWeeksChange={(value) => change({ triggerWeeks: value })}
                        onNogosChange={(value) => change({ triggerNogos: value })}
                    />
                )}
            </div>
            <div className="grid grid-cols-2 gap-3">
                <TeamField
                    team={draft.team}
                    // An assignee belongs to one team, so switching teams drops them.
                    onChange={(team) =>
                        change(team === draft.team ? { team } : { team, assigneeEmail: '' })
                    }
                />
                <VirtualAppointmentAssigneeField
                    team={draft.team}
                    assigneeEmail={draft.assigneeEmail}
                    onChange={(assigneeEmail) => change({ assigneeEmail })}
                />
            </div>
        </div>
    );
}

interface DescriptionFieldProps {
    value: TiptapDoc;
    error?: string;
    onChange: (doc: TiptapDoc) => void;
    onImageUpload: RichEditorImageUploadHandler;
}

// Grows into the panel's remaining height; the editor scrolls inside it.
function DescriptionField({ value, error, onChange, onImageUpload }: DescriptionFieldProps) {
    return (
        <div className="flex-1 min-h-0 flex flex-col">
            <FieldLabel required error={error}>
                Description
            </FieldLabel>
            <VirtualAppointmentDescriptionBox>
                <RichEditor
                    value={value}
                    onChange={onChange}
                    onImageUpload={onImageUpload}
                    placeholder="What should this check look at?"
                    chrome="flush"
                    autoFocus={false}
                />
            </VirtualAppointmentDescriptionBox>
        </div>
    );
}

export function VirtualAppointmentEditor({
    draft,
    errors,
    typeOptions,
    revealErrors,
    onChange,
    onImageUpload,
    hideTrigger = false,
    stackTypeAndTrigger = false,
    treatmentStartDate = null,
    dmLinked = false,
    treatmentStartUnavailableHint,
    offerWeeksFromNow = true,
    savedTriggers = NO_SAVED_TRIGGERS,
    aligners = UNKNOWN_ALIGNER_PROGRESS,
    savedNogo,
    leading
}: VirtualAppointmentEditorProps) {
    const { key } = draft;
    const [touched, setTouched] = useState(false);
    const shown = revealErrors || touched ? errors : {};
    const change = useCallback(
        (patch: VirtualAppointmentDraftPatch) => onChange(key, patch),
        [onChange, key]
    );
    const handleDescriptionChange = useCallback(
        (doc: TiptapDoc) => change({ descriptionDoc: doc }),
        [change]
    );
    const handleBlur = useCallback((event: FocusEvent<HTMLDivElement>) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setTouched(true);
    }, []);
    const triggerBaseline = plannedTriggerBaseline({
        triggerKind: draft.triggerKind,
        weeks: parseWeeksInput(draft.triggerWeeks),
        weeksFrom: draft.weeksFrom,
        keptAnchor: keptWeeksAnchor(parsedTrigger(draft), savedTriggers),
        treatmentStartDate,
        todayIso: todayUtcIso(),
        aligners,
        savedNogo: savedNogo ?? null
    });
    const weeksFrom: WeeksFromChoice | undefined = offerWeeksFromNow
        ? {
              value: draft.weeksFrom,
              treatmentStartUnavailableHint,
              onChange: (value) => change({ weeksFrom: value })
          }
        : undefined;

    return (
        <div
            onBlur={handleBlur}
            className="flex-1 min-h-0 flex flex-col gap-4 px-5 py-4 overflow-y-auto"
        >
            {leading}
            <CompactFields
                draft={draft}
                errors={shown}
                typeOptions={typeOptions}
                change={change}
                hideTrigger={hideTrigger}
                stackTypeAndTrigger={stackTypeAndTrigger}
                triggerBaseline={triggerBaseline}
                dmLinked={dmLinked}
                weeksFrom={weeksFrom}
            />
            <DescriptionField
                value={draft.descriptionDoc}
                error={shown.description}
                onChange={handleDescriptionChange}
                onImageUpload={onImageUpload}
            />
        </div>
    );
}
